import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Switch } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { QcsPaper } from '../common/basic/QcsPaper';
import { QcsTableContainer } from '../common/basic/QcsTableContainer';
import { QcsTableHead } from '../common/basic/QcsTableHead';
import { QcsTableRow } from '../common/basic/QcsTableRow';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { QcsTableBody } from '../common/basic/QcsTableBody';
import { QcsTypography } from '../common/basic/QcsTypography';
import { QcsFormControlLabel } from '../common/basic/QcsFormControlLabel';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { QcsChip } from '../common/basic/QcsChip';
import { QcsTooltip } from '../common/basic/QcsTooltip';
import { ShowQcsTableContent } from '../ShowQcsTableContent';
import { formatDate } from '../../utils/date';
import { apiClientService } from '@api/services/services';
import { resources } from '@api/resources';
import { ApiClientLogDto } from '@api/models/apiClient/ApiClientLogDto';

export const IntegrationLogs: FC<{ apiClientId: string }> = ({ apiClientId }) => {
  const { t, i18n } = useTranslation();
  const [logs, setLogs] = useState<ApiClientLogDto[]>([]);
  const [errorsOnly, setErrorsOnly] = useState(false);
  //Unverified token requests can come from anyone who knows the clientId, so they are hidden unless asked for.
  const [includeUnverified, setIncludeUnverified] = useState(false);

  const loadLogs = useCallback(() => {
    apiClientService.getLogs(apiClientId, { errorsOnly, includeUnverified }).then((res) => setLogs(res.data));
  }, [apiClientId, errorsOnly, includeUnverified]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <QcsTypography variant="body2">{t('integration.apiAccess.logs.description')}</QcsTypography>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <QcsFormControlLabel
          control={<Switch checked={errorsOnly} onChange={(_, checked) => setErrorsOnly(checked)} />}
          label={t('integration.apiAccess.logs.errorsOnly')}
        />
        <QcsFormControlLabel
          control={<Switch checked={includeUnverified} onChange={(_, checked) => setIncludeUnverified(checked)} />}
          label={t('integration.apiAccess.logs.includeUnverified')}
        />
        <QcsLoadingButton variant="outlined" onClick={loadLogs} resourceLoading={resources.apiClientLogs} sx={{ ml: 'auto' }}>
          <RefreshIcon />
        </QcsLoadingButton>
      </Box>

      <QcsPaper>
        <QcsTableContainer>
          <ShowQcsTableContent resource={resources.apiClientLogs} data={logs}>
            <QcsTableHead>
              <QcsTableRow>
                <QcsTableCell>{t('integration.apiAccess.logs.at')}</QcsTableCell>
                <QcsTableCell>{t('integration.apiAccess.logs.request')}</QcsTableCell>
                <QcsTableCell>{t('integration.apiAccess.logs.status')}</QcsTableCell>
                <QcsTableCell>{t('integration.apiAccess.logs.error')}</QcsTableCell>
                <QcsTableCell hideOnMobile>{t('integration.apiAccess.logs.ip')}</QcsTableCell>
                <QcsTableCell hideOnMobile>{t('integration.apiAccess.logs.duration')}</QcsTableCell>
                <QcsTableCell hideOnMobile>{t('integration.apiAccess.keys.kid')}</QcsTableCell>
              </QcsTableRow>
            </QcsTableHead>
            <QcsTableBody>
              {logs.map((log, index) => (
                <QcsTableRow key={index}>
                  <QcsTableCell>{formatDate(i18n, log.at, true, true)}</QcsTableCell>
                  <QcsTableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{`${log.method} ${log.path}`}</QcsTableCell>
                  <QcsTableCell sx={{ color: log.status >= 400 ? 'error.main' : undefined }}>{log.status}</QcsTableCell>
                  <QcsTableCell>
                    {!log.verified && <QcsChip label={t('integration.apiAccess.logs.unverified')} color="warning" sx={{ mr: 1 }} />}
                    {log.errorCode && (
                      <QcsTooltip title={i18n.exists(`errorCodes.${log.errorCode}`) ? t(`errorCodes.${log.errorCode}`) : undefined}>
                        <span>{log.errorCode}</span>
                      </QcsTooltip>
                    )}
                  </QcsTableCell>
                  <QcsTableCell hideOnMobile>{log.ip}</QcsTableCell>
                  <QcsTableCell hideOnMobile>
                    {log.durationMs !== null && t('integration.apiAccess.logs.durationMs', { ms: log.durationMs })}
                  </QcsTableCell>
                  <QcsTableCell hideOnMobile>{log.kid}</QcsTableCell>
                </QcsTableRow>
              ))}
            </QcsTableBody>
          </ShowQcsTableContent>
        </QcsTableContainer>
      </QcsPaper>
    </Box>
  );
};
